import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Every stat tile at the top of a list is a question — how many are overdue, how many are unpaid — so it is also a
 * filter button (prototype §38 "Clicking the numbers"). Click a tile: the table below shows those rows; click it again:
 * the filter comes off. The active tile lives in the URL as `?filter=<key>`, so a drill-through from the Dashboard
 * or a home block lands with the tile already on, and Back takes it off.
 */
export function useStatFilter<K extends string = string>(param = 'filter') {
  const [params, setParams] = useSearchParams();
  const fromUrl = (params.get(param) as K | null) ?? null;
  const [active, setActive] = useState<K | null>(fromUrl);

  // Follow the URL when it changes underneath us (a link to the same page with another ?filter=).
  useEffect(() => { setActive(fromUrl); }, [fromUrl]);

  const set = useCallback((key: K | null) => {
    setActive(key);
    const next = new URLSearchParams(params);
    if (key) next.set(param, key); else next.delete(param);
    setParams(next, { replace: true });
  }, [params, param, setParams]);

  return {
    /** The tile that is on, or null. */
    active,
    /** Click handler for a tile: on → off, off → on (and any other tile off). */
    toggle: (key: K) => set(active === key ? null : key),
    clear: () => set(null),
    /** True for the tile that is on — pass as StatTile `active`. */
    is: (key: K) => active === key,
    /** Props for a StatTile that filters by `key`. */
    tile: (key: K) => ({ active: active === key, onClick: () => set(active === key ? null : key) }),
  };
}
